import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ToggleLeft, ToggleRight, Edit, Key, Link as LinkIcon, Copy, X } from 'lucide-react';
import * as api from '../services/api';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    max_connections: 1,
    expiry: '',
    notes: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createUser(formData);
      setShowModal(false);
      setFormData({
        username: '',
        password: '',
        max_connections: 1,
        expiry: '',
        notes: ''
      });
      loadUsers();
    } catch (error) {
      alert('Failed to create user: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '',
      max_connections: user.max_connections,
      expiry: user.expiry ? user.expiry.split('T')[0] : '',
      notes: user.notes || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      await api.updateUser(selectedUser.id, updateData);
      setShowEditModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      alert('Failed to update user: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setPasswordForm({ new_password: '', confirm_password: '' });
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert('Passwords do not match');
      return;
    }
    try {
      await api.resetUserPassword(selectedUser.id, passwordForm.new_password);
      alert('Password reset successfully');
      setShowPasswordModal(false);
      setSelectedUser(null);
    } catch (error) {
      alert('Failed to reset password: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleShowConnection = async (user) => {
    try {
      const response = await api.getUserConnectionInfo(user.id);
      setConnectionInfo(response.data);
      setSelectedUser(user);
      setShowConnectionModal(true);
    } catch (error) {
      alert('Failed to get connection info: ' + (error.response?.data?.error || error.message));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const handleToggle = async (id) => {
    try {
      await api.toggleUser(id);
      loadUsers();
    } catch (error) {
      alert('Failed to toggle user: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.deleteUser(id);
      loadUsers();
    } catch (error) {
      alert('Delete failed: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>Users</h2>
        <p>Manage sub-accounts and end users</p>
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3>User Accounts ({users.length})</h3>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={16} />
            Add User
          </button>
        </div>

        {users.length === 0 ? (
          <div className="empty-state">
            <h3>No users yet</h3>
            <p>Create your first user account</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Max Connections</th>
                <th>Expiry</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.username}</strong>
                    {user.notes && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        {user.notes}
                      </div>
                    )}
                  </td>
                  <td>{user.max_connections}</td>
                  <td>
                    {user.expiry ? (
                      new Date(user.expiry) < new Date() ? (
                        <span className="badge badge-danger">
                          Expired {new Date(user.expiry).toLocaleDateString()}
                        </span>
                      ) : (
                        <span>{new Date(user.expiry).toLocaleDateString()}</span>
                      )
                    ) : (
                      <span className="badge badge-info">Never</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge badge-${user.status === 'active' ? 'success' : user.status === 'expired' ? 'danger' : 'warning'}`}>
                      {user.status}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => handleToggle(user.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                        title="Toggle Enable/Disable"
                      >
                        {user.enabled ? (
                          <ToggleRight size={24} color="#10b981" />
                        ) : (
                          <ToggleLeft size={24} color="#9ca3af" />
                        )}
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px' }}
                        onClick={() => handleEdit(user)}
                        title="Edit User"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        className="btn btn-warning"
                        style={{ padding: '6px 12px' }}
                        onClick={() => handleResetPassword(user)}
                        title="Reset Password"
                      >
                        <Key size={14} />
                      </button>
                      <button
                        className="btn btn-info"
                        style={{ padding: '6px 12px' }}
                        onClick={() => handleShowConnection(user)}
                        title="Connection Info"
                      >
                        <LinkIcon size={14} />
                      </button>
                      <button
                        className="btn btn-danger"
                        style={{ padding: '6px 12px' }}
                        onClick={() => handleDelete(user.id)}
                        title="Delete User"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add User Modal */}
      {showModal && (
        <Modal onClose={() => setShowModal(false)} title="Add User">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <input
                type="text"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Max Connections</label>
              <input
                type="number"
                value={formData.max_connections}
                onChange={(e) => setFormData({ ...formData, max_connections: parseInt(e.target.value) })}
                min="1"
                max="10"
                required
              />
            </div>

            <div className="form-group">
              <label>Expiry Date (Optional)</label>
              <input
                type="date"
                value={formData.expiry}
                onChange={(e) => setFormData({ ...formData, expiry: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Notes (Optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="3"
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                Create User
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ flex: 1 }}
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <Modal onClose={() => setShowEditModal(false)} title={`Edit User: ${selectedUser.username}`}>
          <form onSubmit={handleEditSubmit}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Password (leave empty to keep current)</label>
              <input
                type="text"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter new password or leave empty"
              />
            </div>

            <div className="form-group">
              <label>Max Connections</label>
              <input
                type="number"
                value={formData.max_connections}
                onChange={(e) => setFormData({ ...formData, max_connections: parseInt(e.target.value) })}
                min="1"
                max="10"
                required
              />
            </div>

            <div className="form-group">
              <label>Expiry Date (Optional)</label>
              <input
                type="date"
                value={formData.expiry}
                onChange={(e) => setFormData({ ...formData, expiry: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Notes (Optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="3"
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                Update User
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ flex: 1 }}
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && selectedUser && (
        <Modal onClose={() => setShowPasswordModal(false)} title={`Reset Password: ${selectedUser.username}`}>
          <form onSubmit={handlePasswordSubmit}>
            <div className="form-group">
              <label>New Password</label>
              <input
                type="text"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="text"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                required
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                Reset Password
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ flex: 1 }}
                onClick={() => setShowPasswordModal(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Connection Info Modal */}
      {showConnectionModal && selectedUser && connectionInfo && (
        <Modal onClose={() => setShowConnectionModal(false)} title={`Connection Info: ${selectedUser.username}`} wide>
          <div style={{ fontSize: '14px' }}>
            <div style={{ marginBottom: '20px', padding: '15px', background: '#fef3c7', borderRadius: '6px' }}>
              <strong>⚠️ Security Note:</strong> You'll need to provide the user's password separately for these URLs to work.
            </div>

            <div className="form-group">
              <label><strong>Server URL</strong></label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input type="text" value={connectionInfo.server_url} readOnly style={{ flex: 1 }} />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => copyToClipboard(connectionInfo.server_url)}
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>

            <div className="form-group">
              <label><strong>Username</strong></label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input type="text" value={connectionInfo.username} readOnly style={{ flex: 1 }} />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => copyToClipboard(connectionInfo.username)}
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>

            <hr style={{ margin: '20px 0' }} />

            <h4>M3U Playlist URL</h4>
            <div className="form-group">
              <div style={{ display: 'flex', gap: '10px' }}>
                <input type="text" value={connectionInfo.m3u_url} readOnly style={{ flex: 1, fontSize: '12px' }} />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => copyToClipboard(connectionInfo.m3u_url)}
                >
                  <Copy size={14} />
                </button>
              </div>
              <small style={{ color: '#6b7280' }}>Replace "PASSWORD" with the actual password</small>
            </div>

            <h4>EPG URL</h4>
            <div className="form-group">
              <div style={{ display: 'flex', gap: '10px' }}>
                <input type="text" value={connectionInfo.epg_url} readOnly style={{ flex: 1, fontSize: '12px' }} />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => copyToClipboard(connectionInfo.epg_url)}
                >
                  <Copy size={14} />
                </button>
              </div>
              <small style={{ color: '#6b7280' }}>Replace "PASSWORD" with the actual password</small>
            </div>

            <hr style={{ margin: '20px 0' }} />

            <h4>IPTV App Setup</h4>
            <div style={{ padding: '15px', background: '#f3f4f6', borderRadius: '6px', marginBottom: '10px' }}>
              <strong>TiviMate / IPTV Smarters:</strong>
              <ul style={{ marginTop: '10px', marginLeft: '20px' }}>
                <li>Connection Type: <strong>Xtream Codes API</strong></li>
                <li>Server: <strong>{connectionInfo.server_url}</strong></li>
                <li>Username: <strong>{connectionInfo.username}</strong></li>
                <li>Password: <strong>[Enter user's password]</strong></li>
              </ul>
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: '100%', marginTop: '20px' }}
              onClick={() => setShowConnectionModal(false)}
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Modal Component
function Modal({ children, onClose, title, wide }) {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        padding: '30px',
        borderRadius: '8px',
        width: '100%',
        maxWidth: wide ? '700px' : '500px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>{title}</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={24} color="#6b7280" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default Users;
